import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { reportApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DialSliders, DialChips, DEFAULT_DIALS } from "@/components/DialSliders";
import { AdminTournaments } from "@/components/AdminTournaments";
import { AdminEmails } from "@/components/AdminEmails";
import { RepDetail } from "@/components/RepDetail";
import ManagerHub from "@/pages/ManagerHub";
import { ChevronRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Loader2, Users, Activity, BookOpen, Bot, CreditCard, Plus, Pencil, Trash2, Tag, Video
} from "lucide-react";

const Metric = ({ icon: Icon, label, value }) => (
  <div className="card-surface rounded-2xl p-5">
    <Icon className="w-5 h-5 text-emerald-600 mb-3" />
    <div className="text-3xl font-extrabold text-slate-900 font-display">{value}</div>
    <div className="text-xs text-slate-500 mt-1">{label}</div>
  </div>
);

const EMPTY_PERSONA = { name: "", title: "", difficulty: "Medium", avatar: "", tagline: "", traits: "", prompt: "" };

export default function Admin() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <ManagerHub />;
  return <AdminStudio />;
}

function AdminStudio() {
  const [data, setData] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [repId, setRepId] = useState(null);

  // persona dialog
  const [pOpen, setPOpen] = useState(false);
  const [pForm, setPForm] = useState(EMPTY_PERSONA);
  const [pEditId, setPEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialsOn, setDialsOn] = useState(false);
  const [dials, setDials] = useState(DEFAULT_DIALS);

  // course + lesson
  const [cTitle, setCTitle] = useState("");
  const [lessonForms, setLessonForms] = useState({});

  const { user } = useAuth();
  const loadAll = () => {
    const warn = reportApiError("Admin");
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(warn);
    api.get("/admin/personas").then((r) => setPersonas(r.data)).catch(warn);
    api.get("/admin/sessions").then((r) => setSessions(r.data)).catch(warn);
    if (user?.role === "admin") {
      api.get("/admin/courses").then((r) => setCourses(r.data)).catch(warn);
      api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(warn);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const openNewPersona = () => { setPForm(EMPTY_PERSONA); setPEditId(null); setDialsOn(true); setDials(DEFAULT_DIALS); setPOpen(true); };
  const openEditPersona = (p) => {
    const has = p.objection_level != null || p.budget != null || p.attitude != null;
    setDialsOn(has);
    setDials({ objection_level: p.objection_level ?? 5, budget: p.budget ?? 5, attitude: p.attitude ?? 5 });
    setPForm({ ...p, traits: (p.traits || []).join(", ") }); setPEditId(p.id); setPOpen(true);
  };
  const savePersona = async () => {
    if (!pForm.title || !pForm.name || !pForm.prompt) return toast.error("Name, title and prompt are required");
    setSaving(true);
    const dialPayload = dialsOn ? dials : { objection_level: null, budget: null, attitude: null };
    const payload = { ...pForm, ...dialPayload, traits: pForm.traits.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (pEditId) await api.put(`/admin/personas/${pEditId}`, payload);
      else await api.post("/admin/personas", payload);
      toast.success(pEditId ? "Persona updated" : "Persona created");
      setPOpen(false); loadAll();
    } catch { toast.error("Couldn't save persona"); } finally { setSaving(false); }
  };
  const deletePersona = async (id) => {
    if (!window.confirm("Delete this persona?")) return;
    await api.delete(`/admin/personas/${id}`); toast.success("Deleted"); loadAll();
  };

  const addCourse = async () => {
    if (!cTitle.trim()) return;
    const level = courses.length + 1;
    try { await api.post("/admin/courses", { title: cTitle.trim(), level }); setCTitle(""); toast.success("Course added"); loadAll(); }
    catch { toast.error("Couldn't add the course"); }
  };
  const deleteCourse = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    try { await api.delete(`/admin/courses/${id}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Couldn't delete the course"); }
  };
  const addLesson = async (cid) => {
    const f = lessonForms[cid] || {};
    if (!f.title) return toast.error("Lesson title required");
    try {
      await api.post(`/admin/courses/${cid}/lessons`, { title: f.title, duration: f.duration || "10 min", summary: f.summary || "", video_url: f.video_url || null });
      setLessonForms((s) => ({ ...s, [cid]: {} })); toast.success("Lesson added"); loadAll();
    } catch { toast.error("Couldn't add the lesson"); }
  };
  const deleteLesson = async (cid, lid) => {
    try { await api.delete(`/admin/courses/${cid}/lessons/${lid}`); loadAll(); }
    catch { toast.error("Couldn't delete the lesson"); }
  };
  const patchLesson = async (cid, lid, patch) => {
    try { await api.patch(`/admin/courses/${cid}/lessons/${lid}`, patch); toast.success("Lesson updated"); loadAll(); }
    catch { toast.error("Couldn't update lesson"); }
  };
  const setLf = (cid, k, v) => setLessonForms((s) => ({ ...s, [cid]: { ...(s[cid] || {}), [k]: v } }));

  if (!data) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const teamScope = data.scope === "team";

  return (
    <div className="space-y-6">
      <div>
        <p className="text-emerald-600 font-mono text-xs uppercase tracking-widest">{teamScope ? "Team manager hub" : "Linden's control room"}</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" data-testid="admin-title">{teamScope ? `${data.team?.name || "Your team"} — Manager tools` : "Content Studio & Admin"}</h1>
        {teamScope && <p className="text-sm text-slate-600 mt-2" data-testid="admin-team-scope-note">You're seeing your team's reps and conversations. Create tournaments for them and preview your Monday digest here.</p>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Metric icon={Users} label={teamScope ? "Team reps" : "Users"} value={data.total_users} />
        <Metric icon={Activity} label="Sessions" value={data.total_sessions} />
        <Metric icon={Bot} label="Personas" value={data.personas} />
        <Metric icon={BookOpen} label="Courses" value={data.courses} />
        {!teamScope && <Metric icon={CreditCard} label="Subscriptions" value={data.paid_subscriptions} />}
      </div>

      <Tabs defaultValue={teamScope ? "users" : "personas"}>
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
          {!teamScope && <TabsTrigger value="personas" data-testid="admin-tab-personas">Personas</TabsTrigger>}
          {!teamScope && <TabsTrigger value="courses" data-testid="admin-tab-courses">Courses</TabsTrigger>}
          <TabsTrigger value="users" data-testid="admin-tab-users">{teamScope ? "My reps" : "Users"}</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="admin-tab-sessions">Conversations</TabsTrigger>
          {!teamScope && <TabsTrigger value="discounts" data-testid="admin-tab-discounts">Discounts</TabsTrigger>}
          <TabsTrigger value="tournaments" data-testid="admin-tab-tournaments">Tournaments</TabsTrigger>
          <TabsTrigger value="emails" data-testid="admin-tab-emails">Emails</TabsTrigger>
        </TabsList>

        <TabsContent value="emails" className="mt-4">
          <AdminEmails />
        </TabsContent>

        <TabsContent value="tournaments" className="mt-4">
          <AdminTournaments personas={personas} />
        </TabsContent>

        {/* PERSONAS */}
        <TabsContent value="personas" className="mt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={pOpen} onOpenChange={setPOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNewPersona} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="new-persona-btn"><Plus className="w-4 h-4 mr-1" /> New Persona</Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 text-slate-900 max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{pEditId ? "Edit" : "Create"} customer persona</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  {[["name", "Name (e.g. Victor Slade)"], ["title", "Title (e.g. Rude Customer)"], ["avatar", "Avatar image URL"], ["tagline", "Tagline"], ["traits", "Traits (comma separated)"]].map(([k, ph]) => (
                    <div key={k}>
                      <Label className="text-slate-600 text-xs capitalize">{k}</Label>
                      <Input value={pForm[k]} onChange={(e) => setPForm({ ...pForm, [k]: e.target.value })} placeholder={ph}
                        className="mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`persona-field-${k}`} />
                    </div>
                  ))}
                  <div>
                    <Label className="text-slate-600 text-xs">Difficulty</Label>
                    <select value={pForm.difficulty} onChange={(e) => setPForm({ ...pForm, difficulty: e.target.value })}
                      className="mt-1 w-full h-10 rounded-md bg-slate-50 border border-slate-200 text-slate-900 text-sm px-3" data-testid="persona-field-difficulty">
                      {["Easy", "Medium", "Hard", "Elite"].map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-600 text-xs">AI Prompt (how this customer behaves)</Label>
                    <Textarea value={pForm.prompt} onChange={(e) => setPForm({ ...pForm, prompt: e.target.value })} rows={5}
                      placeholder="You are... Stay in character as a customer..."
                      className="mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid="persona-field-prompt" />
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-slate-700 text-sm font-semibold">Personality dials</Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">Tune how hard this customer pushes back, how much they can spend, and how they treat the rep.</p>
                      </div>
                      <Switch checked={dialsOn} onCheckedChange={setDialsOn} data-testid="persona-dials-toggle" />
                    </div>
                    {dialsOn && <div className="mt-4"><DialSliders values={dials} onChange={setDials} idPrefix="admin-dial" /></div>}
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={savePersona} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold w-full" data-testid="save-persona-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : pEditId ? "Save changes" : "Create persona"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {personas.map((p) => (
              <div key={p.id} className="card-surface rounded-2xl p-4" data-testid={`admin-persona-${p.id}`}>
                <div className="flex items-center gap-3">
                  <img src={p.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{p.title}</div>
                    <div className="text-xs text-slate-500">{p.name} · {p.difficulty}</div>
                  </div>
                  <button onClick={() => openEditPersona(p)} className="text-slate-500 hover:text-emerald-600" data-testid={`edit-persona-${p.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => deletePersona(p.id)} className="text-slate-500 hover:text-red-600" data-testid={`delete-persona-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <p className="text-xs text-slate-500 mt-3 line-clamp-2">{p.prompt}</p>
                <DialChips persona={p} />
              </div>
            ))}
          </div>
        </TabsContent>

        {/* COURSES */}
        <TabsContent value="courses" className="mt-4 space-y-4">
          <div className="card-surface rounded-2xl p-4 flex gap-2">
            <Input value={cTitle} onChange={(e) => setCTitle(e.target.value)} placeholder="New course title"
              className="bg-slate-50 border-slate-200 text-slate-900" data-testid="new-course-input" />
            <Button onClick={addCourse} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="add-course-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          {courses.map((c) => (
            <div key={c.id} className="card-surface rounded-2xl p-4" data-testid={`admin-course-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center text-black text-xs font-bold" style={{ background: c.color }}>{c.level}</span>
                  <span className="font-semibold text-slate-900">{c.title}</span>
                </div>
                <button onClick={() => deleteCourse(c.id)} className="text-slate-500 hover:text-red-600" data-testid={`delete-course-${c.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-3 space-y-1.5">
                {(c.lessons || []).map((l) => (
                  <div key={l.id} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-100 rounded-lg px-3 py-2 flex-wrap">
                    {l.video_url && <Video className="w-3.5 h-3.5 text-cyan-600" />}
                    <span className="flex-1 min-w-[140px]">{l.title} <span className="text-slate-500">· {l.duration}</span></span>
                    <Input defaultValue={l.video_url || ""} key={`${l.id}-${l.video_url || ""}`} placeholder="Video URL (YouTube / Vimeo / mp4)"
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (l.video_url || "")) patchLesson(c.id, l.id, { video_url: v }); }}
                      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                      className="h-8 text-xs bg-white border-slate-200 text-slate-900 w-full sm:w-64" data-testid={`lesson-video-${l.id}`} />
                    <button onClick={() => deleteLesson(c.id, l.id)} className="text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-3 grid sm:grid-cols-4 gap-2">
                <Input value={(lessonForms[c.id]?.title) || ""} onChange={(e) => setLf(c.id, "title", e.target.value)} placeholder="Lesson title" className="bg-slate-50 border-slate-200 text-slate-900 sm:col-span-1" data-testid={`lesson-title-${c.id}`} />
                <Input value={(lessonForms[c.id]?.duration) || ""} onChange={(e) => setLf(c.id, "duration", e.target.value)} placeholder="Duration" className="bg-slate-50 border-slate-200 text-slate-900" />
                <Input value={(lessonForms[c.id]?.video_url) || ""} onChange={(e) => setLf(c.id, "video_url", e.target.value)} placeholder="Video URL (optional)" className="bg-slate-50 border-slate-200 text-slate-900" />
                <Button onClick={() => addLesson(c.id)} className="bg-slate-100 hover:bg-white/20 text-slate-900" data-testid={`add-lesson-${c.id}`}><Plus className="w-4 h-4 mr-1" /> Lesson</Button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-4">
          <p className="text-xs text-slate-500 mb-2">Tap a rep to see their scorecards, weak skills and transcripts.</p>
          <div className="card-surface rounded-2xl overflow-hidden">
            {data.recent_users.map((u) => (
              <button type="button" onClick={() => setRepId(u.id)} key={u.id} className="w-full text-left flex items-center justify-between px-5 py-3 border-b border-slate-200 last:border-0 hover:bg-emerald-50/60 transition-colors" data-testid={`admin-user-${u.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">{u.name} <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 ml-1">{u.plan || "Free"}</span></div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </div>
                <div className="text-right text-xs text-slate-500 flex items-center gap-3">
                  <div>
                    <div className="font-mono text-emerald-600">{(u.xp || 0).toLocaleString()} XP</div>
                    <div>{u.conversations_completed || 0} convos</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </button>
            ))}
          </div>
          <RepDetail repId={repId} onClose={() => setRepId(null)} />
        </TabsContent>

        {/* SESSIONS */}
        <TabsContent value="sessions" className="mt-4">
          <div className="card-surface rounded-2xl overflow-hidden">
            {sessions.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">No sessions yet.</div>}
            {sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-5 py-3 border-b border-slate-200 last:border-0" data-testid={`admin-session-${s.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">{s.persona_title}</div>
                  <div className="text-xs text-slate-500">{s.status}{s.rating != null ? ` · scored ${s.rating}` : ""}</div>
                </div>
                <span className={`text-xs font-mono ${s.won ? "text-emerald-600" : s.status === "completed" ? "text-red-600" : "text-slate-500"}`}>
                  {s.status === "completed" ? (s.won ? "WON" : "LOST") : "ACTIVE"}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* DISCOUNTS */}
        <TabsContent value="discounts" className="mt-4">
          <div className="grid sm:grid-cols-2 gap-3">
            {discounts.map((d) => (
              <div key={d.code} className="card-surface rounded-2xl p-4 flex items-center gap-3" data-testid={`admin-discount-${d.code}`}>
                <Tag className="w-5 h-5 text-amber-600" />
                <div>
                  <div className="font-mono font-bold text-slate-900">{d.code}</div>
                  <div className="text-xs text-slate-500">{d.label} · applied at checkout</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-600 mt-3">Discount codes are validated at checkout and applied via Stripe coupons.</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
